import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

const createDebitSchema = (t: TFunction) =>
  z.object({
    amount: z.coerce
      .number({ invalid_type_error: t('billing.amountPositive', 'Сумма должна быть больше нуля') })
      .positive(t('billing.amountPositive', 'Сумма должна быть больше нуля')),
    description: z
      .string()
      .trim()
      .min(1, t('billing.debitDescriptionRequired', 'Комментарий обязателен')),
  })

export type BalanceDebitFormData = {
  amount: number
  description: string
}

type Props = {
  open: boolean
  onClose: () => void
  onSubmit: (data: BalanceDebitFormData) => Promise<void>
}

export function BalanceDebitDialog({ open, onClose, onSubmit }: Props) {
  const { t } = useTranslation()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<BalanceDebitFormData>({
    resolver: zodResolver(createDebitSchema(t)),
    defaultValues: { amount: 0, description: '' },
  })

  const handleClose = () => {
    if (isSubmitting) return
    reset({ amount: 0, description: '' })
    onClose()
  }

  const handleFormSubmit = async (data: BalanceDebitFormData) => {
    await onSubmit(data)
    reset({ amount: 0, description: '' })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('billing.debitTitle', 'Списание с баланса')}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  type="number"
                  label={t('billing.amount', 'Сумма')}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                  inputProps={{ min: 0, step: '0.01' }}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  multiline
                  minRows={2}
                  required
                  label={t('billing.description', 'Комментарий')}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button type="submit" variant="contained" color="error" disabled={isSubmitting}>
            {t('billing.debit', 'Списать')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
